import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import OrderAlerts from './OrderAlerts';
import { useLab } from '../i18n/LabContext';
import { useLang } from '../i18n/LanguageContext';
import { useImpersonate } from '../i18n/ImpersonateContext';

// Barcha sahifalar ro'yxati (slug → label, icon)
// Tartib: eng ko'p ishlatiladiganidan (har kun) → sozlamalar/kam ishlatiladiganlar (oxirida)
export const ALL_PAGES = [
  // ── HAR KUN, HAR SOATDA (jonli ish) ──────────────────────────
  { slug: 'dashboard',        icon: '📊', label: 'Dashboard' },
  { slug: 'floor',            icon: '🎮', label: 'Zal' },
  { slug: 'station-rates',   icon: '🎱', label: 'Stanciya tariflari' },
  { slug: 'kassa',            icon: '💰', label: 'Kassa' },
  { slug: 'bar-preorders',    icon: '🍔', label: 'Bar buyurtmalar' },
  { slug: 'sessions',         icon: '⏱️', label: 'Sessiyalar' },
  { slug: 'bookings',         icon: '📅', label: 'Bronlar' },

  // ── HAR KUN (mijozlar bilan ish) ─────────────────────────────
  { slug: 'clients',          icon: '👥', label: 'Mijozlar' },
  { slug: 'announcements',    icon: '📢', label: 'E\'lonlar & Chegirmalar' },
  { slug: 'feedbacks',        icon: '📝', label: 'Shikoyat/Taklif' },
  { slug: 'notifications',    icon: '🔔', label: 'Bildirishnomalar' },

  // ── HAFTADA BIR NECHA MARTA (moliya, xodim) ──────────────────
  { slug: 'finance',          icon: '📊', label: 'Moliya & Analitika' },
  { slug: 'expenses',         icon: '💸', label: 'Xarajatlar' },
  { slug: 'staff',            icon: '👥', label: 'Xodimlar & Oylik' },
  { slug: 'crm',              icon: '👤', label: 'CRM' },

  // ── TEZ-TEZ (sotuv, aksiya, turnir) ──────────────────────────
  { slug: 'snack',            icon: '🍕', label: 'Snack-bar' },
  { slug: 'promotions',       icon: '🎯', label: 'Aksiyalar' },
  { slug: 'topup-bonuses',    icon: '🎁', label: 'To\'ldirish bonusi' },
  { slug: 'refunds',          icon: '↩', label: 'Qaytarish' },
  { slug: 'rewards',          icon: '🏅', label: 'Sovg\'alar' },
  { slug: 'promo',            icon: '🎟️', label: 'Promo / Sodiqlik' },
  { slug: 'memberships',      icon: '💎', label: 'Abonement & HH' },
  { slug: 'tournaments',      icon: '🏆', label: 'Turnirlar' },
  { slug: 'leaderboard',      icon: '🏆', label: 'Leaderboard' },

  // ── KAMDAN-KAM (mahsulot, o'yin katalogi) ────────────────────
  { slug: 'games',            icon: '🎮', label: "O'yinlar" },
  { slug: 'apps',             icon: '🚀', label: 'Dasturlar katalogi' },
  { slug: 'packages',         icon: '📦', label: 'Paketlar' },
  { slug: 'computers',        icon: '🖥️', label: 'Kompyuterlar' },
  { slug: 'pending-devices',  icon: '🆕', label: 'Yangi qurilmalar' },
  { slug: 'diskless',         icon: '💾', label: 'Diskless (NSBoot)' },
  { slug: 'maintenance',      icon: '🔧', label: 'Texnik xizmat' },

  // ── KLUB SOZLAMALARI (bir marta o'rnatish) ───────────────────
  { slug: 'lab-info',         icon: '📸', label: 'Klub ma\'lumot' },
  { slug: 'layout-editor',    icon: '🗺️', label: 'Zal muharriri' },
  { slug: 'launcher-themes',  icon: '🎨', label: 'Launcher dizaynlari' },
  { slug: 'payment-settings', icon: '💳', label: "To'lov sozlamalari" },
  { slug: 'fiscal',           icon: '🧾', label: 'Fiskal KKM' },
  { slug: 'obuna',            icon: '📋', label: 'Obuna' },

  // ── FAQAT KLUB EGASI (admin) ─────────────────────────────────
  { slug: 'access-control',   icon: '🔐', label: 'Kirish huquqlari', ownerOnly: true },
  { slug: 'theme',            icon: '🎨', label: 'Dizayn & Tema',    ownerOnly: true },
  { slug: 'super-admin',      icon: '👑', label: 'Super Admin' },
];

function getRole() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload;
  } catch { return null; }
}

const SUPER_NAV = [
  { to: '/super',               icon: '📊', label: 'Platforma' },
  { to: '/super/labs',          icon: '🏢', label: 'Barcha klublar' },
  { to: '/super/labs/new',      icon: '➕', label: 'Yangi klub' },
  { to: '/super/users',         icon: '👥', label: 'Foydalanuvchilar' },
  { to: '/super/subscriptions', icon: '📋', label: 'Obunalar' },
  { to: '/super/catalog',       icon: '🎮', label: 'Katalog' },
  { to: '/super/clubs',         icon: '🏢', label: 'Klublar katalogi' },
];

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, switchLang } = useLang();
  const { labId, labs, setLabId } = useLab();

  const payload = getRole();
  const isOwner = payload?.role === 'owner';
  const isStaff = payload?.role === 'staff';
  const isSuperAdmin = payload?.sa === true;
  const allowedPages = isStaff ? (payload?.pages || []) : null;

  let visibleItems = ALL_PAGES.filter(p => {
    if (p.ownerOnly) return isOwner;
    if (isOwner) return true;
    if (isStaff) return allowedPages.includes(p.slug);
    return true;
  });

  // Foydalanuvchi tartibini qo'llash (super admindan)
  try {
    const savedOrder = JSON.parse(localStorage.getItem('menu_order') || '[]');
    if (Array.isArray(savedOrder) && savedOrder.length) {
      const map = new Map(visibleItems.map(p => [p.slug, p]));
      const ordered = [];
      for (const slug of savedOrder) if (map.has(slug)) { ordered.push(map.get(slug)); map.delete(slug); }
      for (const p of visibleItems) if (map.has(p.slug)) ordered.push(p);
      visibleItems = ordered;
    }
  } catch {}

  const navItems = visibleItems.map(p => ({
    to: p.slug === 'dashboard' ? '/' : `/${p.slug}`,
    icon: p.icon,
    labelKey: 'nav_' + p.slug.replace(/-/g, '_'),
    label: p.label,
    end: p.slug === 'dashboard',
  }));

  const logout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const { t } = useLang();
  const roleBadge = isOwner ? t('roleOwner') : isStaff ? (payload?.staff_role || t('roleStaff')) : '';
  const { impLab, exit: exitImpersonate } = useImpersonate();

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <aside style={{
        width: 220, background: 'var(--bg2)', borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, bottom: 0
      }}>
        <div style={{ padding: '24px 20px', borderBottom: '1px solid var(--border)' }}>
          <div style={{
            fontFamily: 'Orbitron, sans-serif', fontSize: 18, fontWeight: 900,
            background: 'linear-gradient(135deg, var(--cyan), var(--purple))',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
          }}>⚡ Defuse</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>Admin Panel</div>
            {roleBadge && (
              <span style={{
                fontSize: 9, padding: '1px 6px', borderRadius: 4,
                background: isOwner ? 'rgba(0,212,255,0.15)' : 'rgba(139,92,246,0.15)',
                color: isOwner ? 'var(--cyan)' : 'var(--purple)', fontWeight: 700
              }}>{roleBadge}</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 4, marginTop: 10 }}>
            {['uz', 'ru'].map(l => (
              <button key={l} onClick={() => switchLang(l)} style={{
                padding: '2px 8px', borderRadius: 6, fontSize: 10, cursor: 'pointer',
                background: lang === l ? 'rgba(0,212,255,0.15)' : 'transparent',
                border: `1px solid ${lang === l ? 'var(--cyan)' : 'var(--border)'}`,
                color: lang === l ? 'var(--cyan)' : 'var(--text3)',
              }}>{l.toUpperCase()}</button>
            ))}
          </div>
          {/* Filial tanlash dropdown — faqat egaga ko'rinadi va faqat 2+ klub bo'lsa.
              1 klubli egada dropdown yashirin (tanlashga narsa yo'q), labId
              avtomat o'sha klubga tiralgan (LabContext). Xodimga dropdown yo'q. */}
          {isOwner && labs.length >= 2 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
                📍 {t('branch')} ({labs.length} ta)
              </div>
              <select
                value={labId == null ? 'all' : String(labId)}
                onChange={(e) => {
                  const v = e.target.value;
                  setLabId(v === 'all' ? null : parseInt(v, 10));
                  // Klub almashishi — joriy sahifa holatini yangilash uchun
                  // Outlet key={lab-${labId}} React-router o'zi qayta renderlaydi
                  // (pastdagi <Outlet key=...> ga qarang). Qo'shimcha reload shart emas.
                }}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  background: 'rgba(0,212,255,0.08)',
                  border: '1px solid var(--cyan)',
                  borderRadius: 8,
                  color: 'var(--cyan)',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                <option value="all" style={{ background: 'var(--bg2)', color: 'var(--text)' }}>
                  🌐 {t('allLabs') || 'Hammasi'}
                </option>
                {labs.map(l => (
                  <option key={l.id} value={l.id} style={{ background: 'var(--bg2)', color: 'var(--text)' }}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <nav style={{ flex: 1, padding: '16px 0', overflowY: 'auto' }}>
          {isSuperAdmin && (
            <>
              <div style={{ padding: '6px 20px 4px', fontSize: 9, color: '#f59e0b', fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase' }}>
                👑 Super Admin
              </div>
              {SUPER_NAV.map(item => (
                <NavLink key={item.to} to={item.to} end={item.to === '/super'} style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 20px',
                  color: isActive ? '#f59e0b' : 'var(--text2)',
                  background: isActive ? 'rgba(245,158,11,0.08)' : 'transparent',
                  borderLeft: isActive ? '3px solid #f59e0b' : '3px solid transparent',
                  textDecoration: 'none', fontSize: 13, transition: 'all 0.2s'
                })}>
                  <span>{item.icon}</span> {item.label}
                </NavLink>
              ))}
              <div style={{ margin: '8px 20px', borderTop: '1px solid var(--border)' }} />
            </>
          )}
          {navItems.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end} style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px',
              color: isActive ? 'var(--cyan)' : 'var(--text2)',
              background: isActive ? 'rgba(0,212,255,0.05)' : 'transparent',
              borderLeft: isActive ? '3px solid var(--cyan)' : '3px solid transparent',
              textDecoration: 'none', fontSize: 13, transition: 'all 0.2s'
            })}>
              <span>{item.icon}</span> {t(item.labelKey) || item.label}
            </NavLink>
          ))}
        </nav>

        <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)' }}>
          <button onClick={logout} style={{
            width: '100%', background: 'rgba(255,59,92,0.1)', border: '1px solid var(--red)',
            color: 'var(--red)', padding: '8px', borderRadius: 8, cursor: 'pointer', fontSize: 13
          }}>🚪 {t('logout')}</button>
        </div>
      </aside>

      <main style={{ marginLeft: 220, flex: 1, minHeight: '100vh', overflowX: 'hidden' }}>
        {impLab && (
          <div style={{
            background: 'linear-gradient(90deg, rgba(245,158,11,0.15), rgba(245,158,11,0.08))',
            borderBottom: '2px solid #f59e0b',
            padding: '10px 32px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 16 }}>👁️</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#f59e0b' }}>Ko'rish rejimi:</span>
              <span style={{ fontSize: 13, color: 'var(--text)' }}>{impLab.name}</span>
              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'rgba(245,158,11,0.2)', color: '#f59e0b' }}>READ ONLY</span>
            </div>
            <button onClick={exitImpersonate} style={{
              padding: '6px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 12,
              background: 'rgba(239,68,68,0.15)', border: '1px solid #ef4444', color: '#ef4444',
            }}>✕ Chiqish</button>
          </div>
        )}
        <div style={{ padding: 32 }}>
          <Outlet key={`lab-${labId || 'none'}`} />
        </div>
      </main>
      <OrderAlerts />
    </div>
  );
}
